import { utils } from 'ethers';

export interface EthereumProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  isMetaMask?: boolean;
  chainId?: string;
  selectedAddress?: string | null;
  isConnected?: () => boolean;
  on?: (event: string, callback: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, callback: (...args: unknown[]) => void) => void;
}

export interface EIP712TypeField {
  name: string;
  type: string;
}

export type EIP712Types = Record<string, EIP712TypeField[]>;

export interface EIP712DomainInput {
  name: string;
  version: string;
  chainId: number;
  verifyingContract: string;
  // salt?: string;
}

export interface EIP712Domain extends Omit<EIP712DomainInput, 'chainId'> {
  chainId: number;
}

export interface EIP712TypedData<
  TMessage extends Record<string, unknown> = Record<string, unknown>
> {
  types: EIP712Types;
  primaryType: string;
  domain: EIP712Domain;
  message: TMessage;
}

export interface CreateEIP712TypedDataParams<TMessage extends Record<string, unknown>> {
  types: EIP712Types;
  domain: EIP712DomainInput;
  primaryType: string;
  message: TMessage;
}

const DEFAULT_EIP712_DOMAIN: EIP712TypeField[] = [
  {
    name: 'name',
    type: 'string',
  },
  {
    name: 'version',
    type: 'string',
  },
  {
    name: 'chainId',
    type: 'uint256',
  },
  {
    name: 'verifyingContract',
    type: 'address',
  },
];

export function createEIP712TypedData<TMessage extends Record<string, unknown>>({
  domain,
  primaryType,
  message,
  types,
}: CreateEIP712TypedDataParams<TMessage>): EIP712TypedData<TMessage> {
  const domainType =
    types.EIP712Domain && types.EIP712Domain.length > 0
      ? types.EIP712Domain
      : DEFAULT_EIP712_DOMAIN;

  return {
    types: {
      ...types,
      EIP712Domain: domainType,
    },
    primaryType,
    domain: {
      ...domain,
    },
    message,
  };
}

export function getEthereumProvider(): EthereumProvider | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const ethereum = (window as unknown as { ethereum?: EthereumProvider }).ethereum;

  if (!ethereum) {
    console.warn('No Ethereum provider found. Install MetaMask or use in-app browser.');
    return null;
  }

  return ethereum;
}

export function isProviderAvailable(): boolean {
  return getEthereumProvider() !== null;
}

export async function connectWallet(): Promise<string[]> {
  const provider = getEthereumProvider();
  if (!provider) {
    console.error('No Ethereum provider found. Please install MetaMask or use in-app browser.');
    return [];
  }

  try {
    const accounts = (await provider.request({
      method: 'eth_requestAccounts',
    })) as string[];

    if (!accounts || accounts.length === 0) {
      console.error('No accounts found');
      return [];
    }

    console.log('MetaMask Wallet connected:', accounts[0]);
    return accounts;
  } catch (error) {
    console.error('Failed to connect wallet:', error);
    return [];
  }
}

export async function getAccounts(): Promise<string[]> {
  const provider = getEthereumProvider();

  if (!provider) {
    return [];
  }

  try {
    const accounts = (await provider.request({
      method: 'eth_accounts',
    })) as string[];

    return accounts || [];
  } catch (error) {
    console.error('Failed to get accounts:', error);
    return [];
  }
}

export async function getChainId(): Promise<string> {
  const provider = getEthereumProvider();

  if (!provider) {
    console.error('No Ethereum provider found');
    return '';
  }

  try {
    const chainId = (await provider.request({
      method: 'eth_chainId',
    })) as string;

    return chainId;
  } catch (error) {
    console.error('Failed to get chain ID:', error);
    return '';
  }
}

// param block - Block parameter (default: 'latest'). Can be 'latest', 'earliest', 'pending', or block number
export async function getNonce(address: string, block: string = 'latest'): Promise<number> {
  const provider = getEthereumProvider();

  if (!provider) {
    console.error('No Ethereum provider found');
    return -1;
  }

  if (!address) {
    console.error('Address is required to get nonce');
    return -1;
  }

  try {
    const nonceHex = (await provider.request({
      method: 'eth_getTransactionCount',
      params: [address, block],
    })) as string;

    // Convert hex to decimal
    const nonce = parseInt(nonceHex, 16);
    console.log(`Nonce for ${address}: ${nonce}`);

    return nonce;
  } catch (error) {
    console.error('Failed to get nonce:', error);
    return -1;
  }
}

export async function signMessage(message: string, address: string): Promise<string> {
  const provider = getEthereumProvider();

  if (!provider) {
    console.error('No Ethereum provider found');
    return '';
  }

  try {
    const signature = (await provider.request({
      method: 'personal_sign',
      params: [message, address],
    })) as string;

    return signature;
  } catch (error) {
    console.error('Failed to sign message:', error);
    return '';
  }
}

export async function signTypedData<TMessage extends Record<string, unknown>>(
  address: string,
  typedData: EIP712TypedData<TMessage>
): Promise<string> {
  const provider = getEthereumProvider();

  if (!provider) {
    console.error('No Ethereum provider found');
    return '';
  }

  try {
    const signature = (await provider.request({
      method: 'eth_signTypedData_v4',
      params: [address, JSON.stringify(typedData)],
    })) as string;

    return signature;
  } catch (error) {
    throw error;
  }
}

export function verifyTypedDataSignature<TMessage extends Record<string, unknown>>(
  address: string,
  typedData: EIP712TypedData<TMessage>,
  signature: string
): boolean {
  if (!address) {
    console.error('Address is required to verify signature');
    return false;
  }

  if (!signature) {
    console.error('Signature is required to verify typed data');
    return false;
  }

  try {
    const { EIP712Domain: _domainType, ...messageTypes } = typedData.types || {};
    const recoveredAddress = utils.verifyTypedData(
      typedData.domain as any,
      messageTypes as Record<string, any[]>,
      typedData.message,
      signature
    );
    console.log('recoveredAddress ', {
      recoveredAddress,
      address,
    });
    return recoveredAddress.toLowerCase() === address.toLowerCase();
  } catch (error) {
    console.error('Failed to verify typed data signature:', error);
    return false;
  }
}

export function onAccountsChanged(callback: (accounts: string[]) => void): (() => void) | null {
  const provider = getEthereumProvider();

  if (!provider || !provider.on) {
    return null;
  }

  const handler = (accounts: unknown) => {
    callback(accounts as string[]);
  };

  provider.on('accountsChanged', handler);

  // Return cleanup function
  return () => {
    if (provider.removeListener) {
      provider.removeListener('accountsChanged', handler);
    }
  };
}

export function onChainChanged(callback: (chainId: string) => void): (() => void) | null {
  const provider = getEthereumProvider();

  if (!provider || !provider.on) {
    return null;
  }

  const handler = (chainId: unknown) => {
    callback(chainId as string);
  };

  provider.on('chainChanged', handler);

  // Return cleanup function
  return () => {
    if (provider.removeListener) {
      provider.removeListener('chainChanged', handler);
    }
  };
}

export function chainIdToDecimal(chainIdHex: string): number {
  return parseInt(chainIdHex, 16);
}

export function chainIdToHex(chainIdDecimal: number): string {
  return '0x' + chainIdDecimal.toString(16);
}

export function getTypedDataV4Hash<TMessage extends Record<string, unknown>>(
  typedData: EIP712TypedData<TMessage>
): string {
  const { domain, types, message } = typedData;
  const { EIP712Domain: _domainType, ...messageTypes } = types || {};

  return utils._TypedDataEncoder.hash(
    domain as any,
    messageTypes as Record<string, any[]>,
    message
  );
}

/**
 * Common chain IDs
 */
export const CHAIN_IDS = {
  ETHEREUM_MAINNET: '0x1',
  ETHEREUM_SEPOLIA: '0xaa36a7',
  POLYGON_MAINNET: '0x89',
  BSC_MAINNET: '0x38',
  ARBITRUM_MAINNET: '0xa4b1',
} as const;
